/**
 * chains — the wall's state model, as pure functions over `GET /mine` (adversarial review of plan
 * d, blocking finding B1: the column, the pill and the redirect were computed from snapshots taken
 * at arm time instead of derived from `/mine` by the armed chain).
 *
 * One identity: a CHAIN, keyed by `root_tile_id` (a chain rooted on a wall tile) or `root_id`
 * (`rootGenerationId` — a from-scratch chain, rooted on its own first row). `Wall.tsx` holds a
 * chain key and a direction draft, and nothing else about the work; every value it shows — the
 * column's basis and result, the tile's shimmer, the pill's two slots and line, the body a
 * Generate posts, the return token a sign-in carries — is computed here from the current `/mine`
 * and the loaded pages. Nothing is frozen at arm time.
 *
 * Pure and side-effect free so the model is testable in the node jest environment the repo runs
 * (`__tests__/chains.spec.ts`); the component composes these, it does not restate them.
 */
import {
    MAX_DIRECTION_CHARS,
    RATIO_KEYS,
    type Basis,
    type ContinueRequest,
    type ErrorCode,
    type Identity,
    type MineResponse,
    type RatioKey,
    type Take,
    type TileSummary,
    type WallGenerateKind,
    type WallWordTappedFrom,
} from '@core/wall/contract'
import type {ChainRoot} from '@core/wall/chainRoot'
import type {AllowanceState} from '@core/wall/contract'
import type {HouseTheme} from './theme'
import type {NoticeKind} from '@core/ui/Notice'

export type ChainKey = string

/** The armed chain before its first take exists — the ghost has no root of its own yet. */
export const GHOST_CHAIN_KEY = 'ghost'

export function tileChainKey(tileId: number): ChainKey {
    return `tile:${tileId}`
}

export function scratchChainKey(rootId: string): ChainKey {
    return `scratch:${rootId}`
}

export function chainKeyOf(take: Take): ChainKey {
    return take.root_tile_id !== null ? tileChainKey(take.root_tile_id) : scratchChainKey(take.root_id)
}

/** What `Wall.tsx` holds: the chain, never a copy of what the chain looked like when it was armed. */
export type Armed =
    | {kind: 'ghost'}
    | {kind: 'tile'; tileId: number}
    | {kind: 'scratch'; rootId: string}

/** The armed chain as `GET /chain/:root` names it (`@core/wall/chainRoot`) — `null` for the ghost,
 *  which has no root of its own and therefore no history to ask for. `Armed`'s two non-ghost
 *  members ARE `ChainRoot`, so this is a narrowing, not a mapping. */
export function armedChainRoot(armed: Armed): ChainRoot | null {
    return armed.kind === 'ghost' ? null : armed
}

export function armedChainKey(armed: Armed): ChainKey {
    if (armed.kind === 'ghost') return GHOST_CHAIN_KEY
    if (armed.kind === 'tile') return tileChainKey(armed.tileId)
    return scratchChainKey(armed.rootId)
}

export type Chain = {
    key: ChainKey
    rootTileId: number | null
    rootId: string
    /** `Take.root` — the server's own join, present on every row of a tile chain whether or not the
     *  tile is on a loaded wall page (B3/S5: the wall must never need the page to name the root). */
    root: TileSummary | null
    /** The chain's newest `done` row: what the column and the tile actually show. */
    newestDone: Take | null
    /** The chain's newest `queued`/`rendering` row: what makes the tile shimmer. */
    running: Take | null
    /** The chain's newest unseen `failed` row (the pill says `failed` once, then `/seen` stamps it). */
    failedUnseen: Take | null
    /** Newest `created_at` across every row of the chain, ms. */
    latestAt: number
}

/** A take's `created_at`, parsed once — exported for `cells.ts`'s own day-bucketing sort (Iteration
 *  II §1), which orders "unseen first, then newest first" the same way every other newest-first
 *  ordering in this file already does. */
export function at(take: Take): number {
    const t = Date.parse(take.created_at)
    return Number.isNaN(t) ? 0 : t
}

function newer(candidate: Take, current: Take | null): boolean {
    return current === null || at(candidate) > at(current)
}

/**
 * Group every own row the wall knows into its chain. `/mine.done` carries at most one row per chain
 * (the server's DISTINCT ON) for the newest `MINE_ROOT_CAP` chains, `pending` every queued/rendering
 * row plus every unseen failed one. `matched` (a search's own chains) and `byTile` (`GET
 * /mine?tiles=`, the chains rooted on the loaded pages' tiles) name chains beyond that window
 * (`2026_09_24_mine_beyond_the_cap.md`): folded in here so every reader of a chain — the tile's
 * face, the counter, continue, pin — sees them, while `cells.ts` still places the wall from
 * `/mine.done` alone. A chain named twice resolves the usual way: its newest row wins.
 *
 * The same row named twice is a tie, and the first source read wins it: `/mine`'s own arrays come
 * first because they are the ones polled; a by-tile copy is asked once per page session.
 */
export function buildChains(mine: MineResponse | null, byTile: readonly Take[] = []): Map<ChainKey, Chain> {
    const chains = new Map<ChainKey, Chain>()
    if (!mine) return chains

    for (const row of [...mine.done, ...mine.pending, ...(mine.matched ?? []), ...byTile]) {
        const key = chainKeyOf(row)
        let chain = chains.get(key)
        if (!chain) {
            chain = {
                key,
                rootTileId: row.root_tile_id,
                rootId: row.root_id,
                root: null,
                newestDone: null,
                running: null,
                failedUnseen: null,
                latestAt: 0,
            }
            chains.set(key, chain)
        }
        if (row.root && !chain.root) chain.root = row.root
        chain.latestAt = Math.max(chain.latestAt, at(row))
        if (row.status === 'done') {
            if (newer(row, chain.newestDone)) chain.newestDone = row
        } else if (row.status === 'failed') {
            if (row.seen_at === null && newer(row, chain.failedUnseen)) chain.failedUnseen = row
        } else if (newer(row, chain.running)) {
            chain.running = row
        }
    }
    return chains
}

/**
 * The row the pill reports: "what is rendering now, not what was asked last" (BRIEF §11). The queue
 * is serial, so at most one row is `rendering`; with none, the pill names the row at the head of the
 * queue (the oldest `queued`), which is the one about to start.
 */
export function rowInFlight(mine: MineResponse | null): Take | null {
    let rendering: Take | null = null
    let queued: Take | null = null
    for (const row of mine?.pending ?? []) {
        if (row.status === 'rendering') {
            if (newer(row, rendering)) rendering = row
        } else if (row.status === 'queued') {
            if (queued === null || at(row) < at(queued)) queued = row
        }
    }
    return rendering ?? queued
}

/** Every unseen `done` row, newest first — what the pill counts as ready (BRIEF §11's `N ready`)
 *  and what `cells.ts`'s day-0 group leads with. */
export function unseenDone(mine: MineResponse | null): Take[] {
    return (mine?.done ?? []).filter((d) => d.seen_at === null).sort((a, b) => at(b) - at(a))
}

/**
 * The chain the pill speaks for once nothing is happening and nothing is unseen (Iteration II §3,
 * BRIEF § Docked amended 2026-09-04: "the pill is present from the first chain until ×" — before
 * this the pill simply returned nothing once the queue was quiet and everything had been seen).
 * `Wall.tsx`'s pill memo tries `notice`, `inFlight`, then `unseenDone` first; this is the fourth
 * and last branch, naming the user's newest chain whatever its state so there is always something
 * to return to once anything of theirs exists. `mine.done` already arrives newest-chain-first
 * (`MineResponse`'s own doc), so its head is the answer whenever a done row exists at all; a chain
 * with no done row yet (still queued or rendering — a fresh Generate, or a batch not yet landed)
 * falls back to the newest `pending` row across every chain.
 */
export function newestOwnRow(mine: MineResponse | null): Take | null {
    if (mine?.done.length) return mine.done[0]
    let newest: Take | null = null
    for (const row of mine?.pending ?? []) if (newer(row, newest)) newest = row
    return newest
}

/**
 * Every chain's current FACE — the id of the newest done row `ownDone` (`/mine.done`) reports for
 * it, keyed the same way every other chain lookup in this file is (`chainKeyOf`). `cells.ts`'s pin
 * placement (plan `2026_09_04_wall_pin.md`) uses this to skip a pinned take that IS its chain's
 * face: the chain's own cell already shows it, so painting the pinned take again would be the same
 * picture twice. Built with `collapseChains` for the same reason every reader in `cells.ts` already
 * re-asserts "one row per chain" rather than trusting the wire's own DISTINCT ON.
 */
export function chainFaces(ownDone: readonly Take[]): Map<ChainKey, string> {
    const faces = new Map<ChainKey, string>()
    for (const take of collapseChains(ownDone)) faces.set(chainKeyOf(take), take.id)
    return faces
}

/** Whether `take` is currently its own chain's face, per `chainFaces`' own map. */
export function isFace(take: Take, faces: ReadonlyMap<ChainKey, string>): boolean {
    return faces.get(chainKeyOf(take)) === take.id
}

/**
 * ONE CELL PER CHAIN (BRIEF § Where a take lives: a chain shows "their latest take in its own cell
 * — and nothing else at rest"). Operator-verified defect, 2026-08-31: a from-scratch take and its
 * own continuation painted as two cells side by side. `/mine`'s `done` is one row per chain by
 * DISTINCT ON, so this collapse is normally a no-op — but the wall's cells are its own statement of
 * the canon, not an inheritance from the reader: any two rows of one chain (a root row written
 * before `rootGenerationId` was backfilled reaches the client with `root_id` falling back to its own
 * `id`, which is exactly the value its children carry) collapse to the chain's newest done take.
 * Earlier takes stay reachable through the column's counter, never as a second cell.
 */
export function collapseChains(rows: readonly Take[]): Take[] {
    const newest = new Map<ChainKey, Take>()
    for (const row of rows) {
        const key = chainKeyOf(row)
        if (newer(row, newest.get(key) ?? null)) newest.set(key, row)
    }
    return [...newest.values()].sort((a, b) => at(b) - at(a))
}

/**
 * ONE BATCH's cells, in position order (plan e: the wall "lifts the nine rows of that batch to the
 * top in position order"). A batch is nine CHAINS, each rooted on its own basis tile, so this is
 * only about which cells the block holds and in what order — what each cell then SHOWS is the
 * chain's own newest row, read the same way every other cell reads it.
 *
 * `collapseChains` is what keeps a cell from appearing twice once one of the nine is continued from:
 * the child row carries the same `rootTileId` but no `batchId` (`enqueueContinue` never copies one),
 * so the filter below sees the original alone and the cell is still one.
 *
 * Position is the wire's own `Take.position`; `(userId, requestId, position)` is unique, so two rows
 * of one batch cannot share one — `created_at` is the tiebreak that keeps the order total anyway.
 */
export function batchTakes(mine: MineResponse | null, batchId: string | null): Take[] {
    if (!batchId) return []
    const rows = [...(mine?.done ?? []), ...(mine?.pending ?? [])].filter((r) => r.batch_id === batchId)
    return collapseChains(rows).sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || at(a) - at(b))
}

/** A batch's cover — the joined root tile of its first cell. It is what the pill shows in slot 1 for
 *  a page's batch (BRIEF § Docked: "slot 1 = … the pack cover for a page's batch"): nine chains are
 *  ONE piece of work, so the pill names the pack rather than whichever cell reached the queue head.
 *  `rows` is `batchTakes`' output, already in position order. */
export function batchCover(rows: readonly Take[]): TileSummary | null {
    for (const row of rows) if (row.root) return row.root
    return null
}

/**
 * The takes THIS SESSION has seen of each chain, kept across polls — what the column's counter counts
 * (BRIEF § The movement: "the dots = this chain's takes made now" — the counter is that same
 * set, redrawn). `/mine` returns one done row
 * per chain, so the wire alone can never show a history: the second take replaces the first in
 * `done` the moment it renders. The ledger is the wall's own record of what passed through it,
 * grown from every `/mine` and every `POST /continue` reply, and it lives exactly as long as the
 * page does — the permanent history is the expanded view (BRIEF § Expanded, v1.1).
 *
 * Pure, and the same object back when nothing changed (`recordTakes` below), so it never triggers
 * a render of its own.
 */
export type TakeLedger = ReadonlyMap<string, Take>

/**
 * The ledger's one write. Every source of rows goes through it — the poll (`holdTakes`), the
 * chain's own history when it is armed (`GET /chain/:root`, which is what carries a chain's earlier
 * takes across a reload: the ledger itself starts empty on every page), and a `POST /pin` reply
 * (`Wall.tsx`'s `togglePin` — see `pinned_at` below).
 */
export function recordTakes(held: TakeLedger, rows: readonly Take[]): TakeLedger {
    let next: Map<string, Take> | null = null
    for (const row of rows) {
        const known = held.get(row.id)
        // A row is re-recorded when it MOVES (queued → rendering → done, a stamp landing, or a pin
        // toggling) — `pinned_at` is compared alongside `seen_at` for the identical reason: the pin
        // door reads the STEPPED-TO take's own `pinnableTake` off this ledger (`Wall.tsx`'s
        // `armedView`), not off `/mine`, so a pin/unpin that never bumped `status` would otherwise
        // leave the door showing the state it had before the toggle (operator-verified defect,
        // 2026-09-04: the wall's own pinned cell and `/mine.pinned` both updated, the open column's
        // door did not). An unchanged row keeps its identity and the map its own.
        if (known && known.status === row.status && known.seen_at === row.seen_at && known.pinned_at === row.pinned_at) {
            continue
        }
        next ??= new Map(held)
        next.set(row.id, row)
    }
    return next ?? held
}

export function holdTakes(held: TakeLedger, mine: MineResponse | null): TakeLedger {
    return recordTakes(held, [...(mine?.done ?? []), ...(mine?.pending ?? [])])
}

/** `wall_generation_done`'s own once-per-poll input (decision 6, plan `2026_09_21_wall_gaps.md`) —
 *  the DONE rows the ledger still remembers as `queued`/`rendering`, i.e. exactly the rows that
 *  just made that transition since the ledger was last held. Read `held` BEFORE the same tick's
 *  own `holdTakes` call folds `done` into it, or every row already reads `done` in both places and
 *  nothing is ever reported (`Wall.tsx`'s own call order does this correctly — see that effect's
 *  header doc). Pure, so the transition is testable without a DOM. */
export function justTurnedDone(held: TakeLedger, done: readonly Take[]): Take[] {
    return done.filter((row) => {
        const prior = held.get(row.id)
        return !!prior && (prior.status === 'queued' || prior.status === 'rendering')
    })
}

/** One chain's takes, oldest first, the newest last — the takes half of `chainSteps`. A `failed` row is not
 *  a take: it has no picture and never will (BRIEF §8, "the tile keeps what it showed"), so it is
 *  not counted. */
export function chainHistory(held: TakeLedger, key: ChainKey): Take[] {
    return [...held.values()]
        .filter((take) => take.status !== 'failed' && chainKeyOf(take) === key)
        .sort((a, b) => at(a) - at(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/**
 * A STEP of the armed history. A chain rooted on a wall tile begins at that tile: it is the picture
 * every take of the chain descends from, and travelling the history without it showed the twists
 * and never the thing twisted (operator-verified defect, 2026-08-31 — the counter counted takes,
 * and a take ledger holds only rows the server made). So the ordered list the counter walks is
 * `basis, take 1 … take N`, and the column reads its picture, its ratio and its next Generate off
 * whichever step it is parked on.
 *
 * The basis is a `TileSummary`, not a `Take`: it has no status, no direction and no id on the wire,
 * so it carries a synthetic step id of its own (`basisStepId`) — namespaced with a colon, which a
 * generation cuid never contains, so the two id spaces cannot collide inside one selection.
 *
 * A from-scratch chain has no basis (nothing was twisted), so its first take is step 1 and the list
 * is exactly what it was before.
 */
export type ChainStep =
    | {kind: 'basis'; id: string; tile: TileSummary}
    | {kind: 'take'; id: string; take: Take}

export function basisStepId(tileId: number): string {
    return `basis:${tileId}`
}

export function takeStep(take: Take): ChainStep {
    return {kind: 'take', id: take.id, take}
}

/** The chain's steps, oldest first: the basis tile when there is one, then `chainHistory`'s takes. */
export function chainSteps(history: readonly Take[], basis: TileSummary | null): ChainStep[] {
    const steps: ChainStep[] = history.map(takeStep)
    if (basis) steps.unshift({kind: 'basis', id: basisStepId(basis.id), tile: basis})
    return steps
}

/** The step the column draws: the one the counter points at, or — nothing selected — the chain's
 *  newest. A selection this chain does not hold (an arm moved on) falls back to the newest too. */
export function displayedStep(steps: readonly ChainStep[], selectedId: string | null): ChainStep | null {
    if (selectedId) {
        const picked = steps.find((step) => step.id === selectedId)
        if (picked) return picked
    }
    return steps.length > 0 ? steps[steps.length - 1] : null
}

/**
 * The step `armedView` shows: what the counter points at and what the frame/result URL are read
 * off. Stepped to a particular step — the user pressed `‹ ›` or tapped the counter — that step and
 * nothing else, so a Generate from there twists a fresh sibling of IT, never of whatever happens to
 * be rendering now (BRIEF § Regenerate: "Continue from an earlier take"). Not stepped, the chain's
 * own IN-FLIGHT row when there is one, else its newest done row, else nothing.
 *
 * Operator-verified defect, 2026-09-02: `chainHistory`/`chainSteps` already count a queued or
 * rendering row into the chain's `total` the instant `/continue`'s reply is merged (`holdTakes`), so
 * pinning the un-stepped case to `newestDone` left the counter reading `N / N+1` with `N` lit for the
 * whole render — the DONE row's own step, one place short of the row actually shimmering
 * (`Counter.tsx`'s unlit-while-rendering state never triggering because the row it was shown was
 * already done).
 */
export function shownStep(
    picked: ChainStep | null,
    stepped: boolean,
    chain: Pick<Chain, 'running' | 'newestDone'> | null | undefined,
): ChainStep | null {
    if (stepped) return picked
    if (chain?.running) return takeStep(chain.running)
    if (chain?.newestDone) return takeStep(chain.newestDone)
    return null
}

/**
 * What the counter needs to draw `‹ 3/5 ›` and what the arrow keys need to step (board
 * `ColumnFurniture`, frames 2–4): the step on screen, its 1-based place in the chain, and the two
 * neighbours — `null` at either end, which is what disables the stepper there. Presentation only:
 * the answer is `displayedStep`'s, read positionally.
 */
export type ChainTravel = {
    /** 0-based place of the step on screen; -1 for an empty chain. */
    index: number
    total: number
    step: ChainStep | null
    prev: ChainStep | null
    next: ChainStep | null
}

export function chainTravel(steps: readonly ChainStep[], selectedId: string | null): ChainTravel {
    const step = displayedStep(steps, selectedId)
    const index = step ? steps.findIndex((one) => one.id === step.id) : -1
    return {
        index,
        total: steps.length,
        step,
        prev: index > 0 ? steps[index - 1] : null,
        next: index >= 0 && index < steps.length - 1 ? steps[index + 1] : null,
    }
}

/**
 * The lift-hold (`holdLifted`/`liftedDone`, re-review N1) is retired by Iteration II (plan
 * `2026_09_04_wall_iteration_ii_placement.md` § The rule): a stamp landing must still not move what
 * the user is looking at, but `cells.ts`'s day-bucketing already gives that for free — `now` is
 * read once per page load (`Wall.tsx`'s ref, never per render), and a row's `seen_at` can only be
 * SET after that moment during the session, so `(now - seenAt)` is clamped to zero rather than
 * going negative and the row stays in day 0 exactly where it already was painted. Nothing needs to
 * remember what it lifted; the clock itself holds still.
 */

/**
 * The take a Generate continues from: the chain's newest done take, or — for a from-scratch chain
 * whose root has not finished yet — the chain's own root row. Never null for a scratch chain, so
 * `continueBody` can never open a SECOND root for one the user already started (S4).
 */
export function parentTakeId(
    armed: Armed,
    chains: Map<ChainKey, Chain>,
    /** The take the counter points at, when the user has stepped back to an earlier one (BRIEF §
     *  Regenerate: "Continue from an earlier take … in v1 the column's dots ‹ › then Generate").
     *  The displayed take IS the parent; with an empty field it is the sibling's source. */
    displayedTakeId: string | null = null,
): string | null {
    if (displayedTakeId) return displayedTakeId
    const chain = chains.get(armedChainKey(armed))
    if (chain?.newestDone) return chain.newestDone.id
    if (armed.kind === 'scratch') return armed.rootId
    return null
}

export function clampDirection(direction: string): string {
    return direction.trim().slice(0, MAX_DIRECTION_CHARS)
}

/** The verb button's two words (design board `VerbLabel.dc.html`, operator-validated 2026-09-01). */
export type VerbState = 'again' | 'generate'

/**
 * Whether the take on screen agrees with the identity the column is armed under — the ONE predicate
 * `verbLabel` and `continueBody` both read, so the button's word and the body it posts can never
 * disagree (amendment, `thinking/plans/2026_09_18_ovid_wall_polish.md`: the operator-reported
 * defect where changing the muse in the bar left the verb reading `Again` while a continue from a
 * take silently re-cast onto the OLD muse, `queue.server.ts`'s carry outrunning what the button
 * said it would do).
 *
 * True — nothing to disagree about — for a non-`'muse'` identity (Toile's `{kind:'none'}`, Narcis's
 * `'model'`), a `'muse'` identity with no muse chosen yet (`slug: null` — `Identity`'s own doc: "the
 * column opens in the choose state"), and any step that is not a take (a basis step, or no step at
 * all: there is no cast muse there to compare against). False only when the step IS a take and the
 * identity names a DIFFERENT muse than the one stamped on it (`Take.muse_slug`, `contract.ts:123`).
 */
export function museMatches(step: ChainStep | null, identity?: Identity | null): boolean {
    if (!identity || identity.kind !== 'muse' || identity.slug === null) return true
    return step?.kind !== 'take' || step.take.muse_slug === identity.slug
}

/**
 * The verb's word: a pure readout of the step on screen, the trimmed field, and (Ovid) whether the
 * armed identity still agrees with the take on screen. `Again` only when the displayed step is a
 * TAKE, the field is empty, AND `museMatches` — exactly the (step, field, identity) triple under
 * which `continueBody` below posts `{again: parent}` for that same step handed in as `from`. A
 * mismatched muse reads `Generate` even with an empty field: changing the muse re-casts the take on
 * screen with her rather than repeating it as a sibling of the old one (the reversal this amendment
 * makes — the verbs plan's § Decided 2 had the carry win unconditionally). A basis step has no
 * parent to repeat (both `armed.kind === 'tile'` on a tile never twisted and a stepped-back basis
 * fall through to `continueBody`'s `{basis:{tile}}` branch), and a step of `null` — the ghost, or a
 * chain with no step yet — has nothing to repeat either, so both read `Generate` whatever the field
 * says, whatever the identity.
 *
 * The ratio pick plays no part: overruling the chain's inherited ratio is still a sibling of the
 * take on screen (BRIEF row "empty, but a different ratio picked" — `continueBody` carries the pick
 * on the `again` body without changing its `kind`).
 *
 * Whitespace-only counts as empty — the same `.trim()` the wire itself applies in `appendDirection`
 * (`prompt.server.ts`), not a second rule invented on the client. `identity` defaults to `undefined`
 * — the house-agnostic reading `museMatches` already gives it (Toile never passes one, and its
 * behaviour here is byte-identical to before this amendment).
 */
export function verbLabel(step: ChainStep | null, field: string, identity?: Identity | null): VerbState {
    return step?.kind === 'take' && field.trim() === '' && museMatches(step, identity) ? 'again' : 'generate'
}

/**
 * The verb word across renders: `verbLabel` recomputed live, except while the step on screen is
 * still rendering AND the field is empty — held at whatever it read when the render started, so a
 * "words typed" Generate (which clears the field on success, before the new take supersedes the
 * old one as `chain.newestDone`) never flickers to `Again` mid-render and back. The hold exists
 * ONLY to stop that one flicker, not to freeze `Again` while the user is typing: the moment `live`
 * itself reads `generate` (words are back in the field, whether typed fresh or never cleared), the
 * hold has nothing left to protect and the live word wins outright — typing during a still-running
 * `Again` must show `Generate` at once, not wait for the render to finish. A freshly armed chain
 * always gets its own live word regardless — even one already mid-render at open, the pill's
 * Continue onto work still in progress — never whatever the PREVIOUS armed chain last held.
 *
 * `Wall.tsx` is the one caller, holding `held`/`freshArm` across renders in refs (an IDENTITY
 * comparison of the armed chain, not a value one — every `arm()` call makes a fresh `Armed` object
 * even for the same tile), which is why the rule is written as an explicit transition rather than a
 * plain memo.
 */
export function holdVerb({
    live, working, held, freshArm,
}: {
    /** `verbLabel` computed fresh this render, from the step on screen and the field as it stands. */
    live: VerbState
    /** Whether the step on screen is still rendering (`armedView.working`). */
    working: boolean
    /** What the previous render held. */
    held: VerbState
    /** True exactly for the render an `arm()` call opens — whether or not that chain is already
     *  mid-render. */
    freshArm: boolean
}): VerbState {
    return freshArm || !working || live === 'generate' ? live : held
}

/**
 * Generate is one verb (BRIEF §7), so one function answers what it posts — from the chain being
 * fired and the field as it stands, never from an arm-time snapshot (S4):
 *
 *   empty field, chain has a take, muse matches (or none to match)  -> `{again}` a sibling of the take
 *   empty field, chain has a take, muse MISMATCHES (Ovid)          -> `{basis:{take}, direction:''}`
 *                                                                      a CHILD of the take, re-cast
 *   words, chain has a take         -> `{basis:{take}}`  the displayed take becomes the parent
 *   words, fresh tile chain         -> `{basis:{tile}}`
 *   words, the ghost                -> `{basis:null, ratio}`
 *
 * The muse-mismatch row (amendment, `2026_09_18_ovid_wall_polish.md`) reads `museMatches` against
 * the SAME step `verbLabel` reads for this render — `shownStep` run over `from`/`chains` exactly as
 * `Wall.tsx`'s own `armedView.displayed` is (never a value merely inferred here), so the button's
 * word and the body posted cannot disagree. A mismatch still posts an EMPTY direction (nothing was
 * typed — this branch only fires when the field is empty) but drops the `again` shape: the new take
 * is a child of the one on screen, cast with the identity the caller is armed under, not a sibling
 * inheriting the old one's `museSlug` the way `enqueueAgain` unconditionally would.
 *
 * Parked on the BASIS step of a tile chain, `from` says so and the answer is the fresh tile chain's
 * — `{basis:{tile}}`, with or without words: stepping back to the tile and firing is a new twist
 * from the tile, indistinguishable on the wire from twisting a tile nobody has twisted yet. It
 * lands as the newest take of the same chain, because the chain is keyed on the tile.
 *
 * `ratioPick` is the ratio the user chose in the armed bar, or `null` when they left it alone
 * (operator decision 2026-08-31, which supersedes "only from the ghost does Toile ask": the ratio
 * is changeable at any point of a chain, so a chain's takes may differ in shape). It rides EVERY
 * shape of the request — a take-parented continue, an `again`, and the muse-mismatch child alike —
 * because a pick is the user overruling the inheritance the chain would otherwise carry. Left alone,
 * no `ratio` is sent at all and the server's own inheritance stands (the parent's stored
 * width/height): the wall never re-states a ratio it merely read, which is what keeps a 1536x640
 * parent from being rounded to the nearest preset on its next take. The ghost is the one shape with
 * nothing to inherit, so it always names one — the pick, or the house default handed in.
 */
export function continueBody({
    armed, chains, direction: rawDirection, ratioPick, houseRatio, from = null, identity = null,
}: {
    armed: Armed
    chains: Map<ChainKey, Chain>
    direction: string
    ratioPick: RatioKey | null
    houseRatio: RatioKey
    /** The step the column is parked on (`displayedStep`), or `null` for a fire that carries no
     *  history of its own — the hover door, which arms and fires the tile under the cursor. */
    from?: ChainStep | null
    /** The identity the column is armed under (`Wall.tsx`'s own `identity`) — `undefined`/`null` at
     *  Toile, which never disagrees with anything (`museMatches`' own doc). */
    identity?: Identity | null
}): {body: ContinueRequest; kind: WallGenerateKind} {
    const direction = clampDirection(rawDirection)
    // On the basis there is no parent take, which is exactly the state of a tile never twisted:
    // both fall through to the `{basis:{tile}}` branch below.
    const parent = from?.kind === 'basis' ? null : parentTakeId(armed, chains, from?.id ?? null)
    const ratio = ratioPick ?? undefined

    if (direction === '' && parent) {
        // The same step `verbLabel` read for this render — not stepped, `shownStep` falls to the
        // chain's running/newest-done row exactly as `Wall.tsx`'s `armedView.displayed` does.
        const displayed = shownStep(from, from !== null, chains.get(armedChainKey(armed)))
        if (museMatches(displayed, identity)) return {body: {again: parent, ...(ratio ? {ratio} : {})}, kind: 'again'}
        return {body: {basis: {take: parent}, direction: '', ...(ratio ? {ratio} : {})}, kind: 'single'}
    }
    if (armed.kind === 'ghost') {
        return {body: {basis: null, direction, ratio: ratioPick ?? houseRatio}, kind: 'from_ghost'}
    }
    if (parent) return {body: {basis: {take: parent}, direction, ...(ratio ? {ratio} : {})}, kind: 'single'}
    // Only a tile chain reaches here: `parentTakeId` never returns null for a scratch chain.
    return {
        body: {basis: {tile: armed.kind === 'tile' ? armed.tileId : 0}, direction, ...(ratio ? {ratio} : {})},
        kind: 'single',
    }
}

/**
 * A `continueBody` wire body, serialised so two calls that would post the identical body compare
 * equal — the accidental-double-tap guard's only notion of "the same press" (operator decision
 * 2026-09-01: everything a deliberate repeat could vary — direction, ratio, basis/parent — lives in
 * `body`, so comparing the body IS comparing the press. An `again` body and a `basis` body are
 * already distinguished by their own top-level key (`again` vs `basis`), so `kind` need not ride
 * along separately.
 *
 * `JSON.stringify` alone is not enough: object key order follows insertion order, and
 * `continueBody`'s branches build the same logical body with different literal orders (a `ratio`
 * spread before or after `direction`, say). Keys are sorted at every level so the key carries only
 * the body's VALUE, never the branch that happened to construct it.
 */
export function pressKeyOf(body: ContinueRequest): string {
    return sortedJSON(body)
}

function sortedJSON(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value)
    if (Array.isArray(value)) return `[${value.map(sortedJSON).join(',')}]`
    const record = value as Record<string, unknown>
    const keys = Object.keys(record).sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${sortedJSON(record[k])}`).join(',')}}`
}

/** One press, as the accidental-double-tap guard below needs to see it: the body it would post,
 *  and when. */
export type Press = {bodyKey: string; at: number}

/**
 * The accidental double-tap (operator decision 2026-09-01): a press is a duplicate of the previous
 * ACCEPTED press — never of its response — when it would post the identical wire body within
 * `windowMs` of that press firing. `fire()` holds one `Press | null` (the last press it accepted)
 * and drops any press this returns `true` for, before `track`/`setFiring`/the fetch — so a dropped
 * press leaves no trace: no analytics event, no shimmer, no queued row. N deliberate presses of
 * DIFFERENT bodies still make N takes (BRIEF canon: "one queue per user, serial, no caps") — this
 * guards only the same body, twice, too fast to have been two decisions.
 *
 * The window is measured from `prev.at` (when the previous press was ACCEPTED), not from when its
 * `/continue` resolved — a slow response must not widen the window a fast double-tap lands inside.
 */
export function isDuplicatePress(prev: Press | null, next: Press, windowMs = 400): boolean {
    return prev !== null && next.bodyKey === prev.bodyKey && next.at - prev.at < windowMs
}

/** The frame the armed view draws — width and height in the same units `TileSummary`/`Take` carry
 *  (stored pixels), read only as a ratio. */
export type Aspect = {width: number; height: number}

const RATIO_ASPECTS: Record<RatioKey, Aspect> = {
    '1:1': {width: 1, height: 1},
    '2:3': {width: 2, height: 3},
    '3:4': {width: 3, height: 4},
    '16:9': {width: 16, height: 9},
    '9:16': {width: 9, height: 16},
    '21:9': {width: 21, height: 9},
}

export function ratioAspect(ratio: RatioKey): Aspect {
    return RATIO_ASPECTS[ratio] ?? RATIO_ASPECTS['2:3']
}

function ratioValue(aspect: Aspect): number {
    return aspect.width > 0 && aspect.height > 0 ? aspect.width / aspect.height : 2 / 3
}

/**
 * Which preset a stored picture is closest to — the pill the armed bar shows as selected when the
 * user has picked nothing and the frame simply states what the chain already is. Compared in log
 * space so the distance is proportional (3:4 → 1:1 is the same step up as 1:1 → 4:3 would be down),
 * which a plain difference of ratios is not.
 */
export function nearestRatioKey(aspect: Aspect, keys: readonly RatioKey[] = RATIO_KEYS): RatioKey {
    const target = Math.log(ratioValue(aspect))
    let best: RatioKey = keys[0] ?? '2:3'
    let bestDistance = Infinity
    for (const key of keys) {
        const distance = Math.abs(Math.log(ratioValue(ratioAspect(key))) - target)
        if (distance < bestDistance) {
            bestDistance = distance
            best = key
        }
    }
    return best
}

/** The pill the armed bar shows lit: the user's own pick, or the shape of what is on screen. */
export function effectiveRatio(ratioPick: RatioKey | null, aspect: Aspect, keys?: readonly RatioKey[]): RatioKey {
    return ratioPick ?? nearestRatioKey(aspect, keys)
}

/**
 * The ratio the column and the sheet draw their frame at (re-review N14: it was hard-wired to 2:3,
 * so a 21:9 basis was armed inside a portrait box and its result letterboxed into one). The frame
 * always states what the user is about to get, in this order:
 *
 *   1. the row being made right now, when the caller hands one in — the shimmer is that picture's
 *      own box, so it wears the INCOMING shape, never the shape of the take it replaces (operator-
 *      verified defect, 2026-08-31: pick 16:9, fire, and the frame fell back to the previous take's
 *      2:3 for the whole render — `Wall.tsx` clears the pick the moment the row lands, and the row
 *      lost to the displayed take here. A queued row carries its own resolved `width`/`height` from
 *      `queue.server.ts`'s `resolveDims`, so the incoming shape is on the wire from the first poll;
 *      between the click and the row's first appearance the pick is still held, which is rule 2).
 *      It is handed in rather than read off the chain because two questions share this function:
 *      what the frame SHOWS (the picture in flight) and what the next take WILL BE (the ratio
 *      `wall_generate` reports, which inherits from the parent, not from a row already rendering);
 *   2. the ratio the user just picked — the pill is a statement about the NEXT take, so the frame
 *      re-fits under it at once (operator decision 2026-08-31: the ratio is changeable at any point
 *      of a chain, not only at the ghost);
 *   3. the basis tile's own ratio, when the counter is parked on the basis step — the frame holds
 *      the picture it is showing, and a twist from the tile inherits the tile's shape;
 *   4. the take the counter points at, then the chain's own newest row — a done take, or the one
 *      rendering: a continue keeps the parent's stored `width`/`height` unless a pick overrules it,
 *      so this IS the picture about to appear;
 *   5. the basis picture's own ratio, for a tile chain that has no take yet;
 *   6. the house default, for a from-scratch line of work with no picture behind it yet.
 *
 * Landing changes nothing: the done row keeps the dims its queued row was written with. A failure
 * takes the row out of `running`, so the frame falls back down this same order — to the take on
 * screen, never to a third shape.
 */
export function armedAspect({
    armed, chains, basis, displayed = null, inFlight = null, ratioPick, houseRatio,
}: {
    armed: Armed
    chains: Map<ChainKey, Chain>
    basis: TileSummary | null
    /** The step on screen (`displayedStep`) — a take, or the chain's basis tile. */
    displayed?: ChainStep | null
    /** The armed chain's queued/rendering row, when the frame is showing the work rather than an
     *  earlier step the counter is parked on. */
    inFlight?: Take | null
    ratioPick: RatioKey | null
    houseRatio: RatioKey
}): Aspect {
    if (inFlight && inFlight.width > 0 && inFlight.height > 0) {
        return {width: inFlight.width, height: inFlight.height}
    }
    if (ratioPick) return ratioAspect(ratioPick)
    if (displayed?.kind === 'basis' && displayed.tile.width > 0 && displayed.tile.height > 0) {
        return {width: displayed.tile.width, height: displayed.tile.height}
    }
    const chain = chains.get(armedChainKey(armed))
    const row = (displayed?.kind === 'take' ? displayed.take : null) ?? chain?.newestDone ?? chain?.running ?? null
    if (row && row.width > 0 && row.height > 0) return {width: row.width, height: row.height}
    if (basis && basis.width > 0 && basis.height > 0) return {width: basis.width, height: basis.height}
    return ratioAspect(houseRatio)
}

/** The basis a `wall_tile_armed` event names, and what the redirect token carries. */
export function armedBasis(armed: Armed, chains: Map<ChainKey, Chain>): Basis {
    if (armed.kind === 'ghost') return null
    const parent = parentTakeId(armed, chains)
    if (armed.kind === 'scratch') return {take: parent ?? armed.rootId}
    return {tile: armed.tileId}
}

/** `tile_id` on `wall_tile_armed`/`wall_generate`/`wall_word_tapped` (decision 2, plan
 *  `2026_09_21_wall_gaps.md`) — the numeric tile id of the basis, or the chain's own root tile,
 *  `null` for a from-scratch ghost (which has neither yet). Distinct from `armedBasis` above: that
 *  one names a TAKE for a scratch chain (the redirect token's own vocabulary); this one always
 *  answers with a TILE id or nothing, which is what a numeric GA4 dimension can hold. */
export function armedTileId(armed: Armed, chains: Map<ChainKey, Chain>): number | null {
    if (armed.kind === 'ghost') return null
    if (armed.kind === 'tile') return armed.tileId
    return chains.get(armedChainKey(armed))?.rootTileId ?? null
}

/** `kind` on `wall_generation_done` (decision 6, plan `2026_09_21_wall_gaps.md`) — reconstructed
 *  from a DONE take's own persisted shape, since the fire-time `kind` (`continueBody`'s own return)
 *  never rides the wire to completion. `again` (a same-words sibling) and a same-direction `single`
 *  both leave `direction: ''` with a parent and are indistinguishable once landed, so both read
 *  `single` here — an honest simplification, not a second literal list to keep in sync with
 *  `WallGenerateKind`. */
export function takeGenerateKind(take: Take): WallGenerateKind {
    if (take.batch_id !== null) return 'pack'
    if (take.root_tile_id === null && take.parent_id === null) return 'from_ghost'
    return 'single'
}

/**
 * The wall's own URL after sign-in (`ReturnToken`, contract): the chain being fired plus the words
 * as typed. Built from the chain handed in — a hover-Generate builds it from the tile under the
 * cursor, not from whatever was armed before (S3).
 */
export function returnTokenFor(armed: Armed, rawDirection: string, ratioPick: RatioKey | null): string {
    const params = new URLSearchParams()
    if (armed.kind === 'tile') params.set('basis', String(armed.tileId))
    const direction = clampDirection(rawDirection)
    if (direction) params.set('direction', direction)
    // A ratio the user PICKED survives the card, whatever the basis (the ratio is now askable at
    // any point of a chain); one merely inherited from the basis is re-derived on the way back and
    // does not belong in the URL.
    if (ratioPick) params.set('ratio', ratioPick)
    const qs = params.toString()
    return qs ? `/?${qs}` : '/'
}

/**
 * The pill's line for a `/continue` that did not land (B2). Same vocabulary as the states — short,
 * lowercase, no punctuation: a 5xx or a dead connection is indistinguishable from a render that
 * failed, so it reads `failed`; a 4xx names what the user can do about it.
 */
/**
 * Whether the column's verb may fire for the given identity (plan `2026_09_17_ovid_wall_verbs.md`
 * § Decided 5) — a pure readout of `Identity`'s own documented meaning (`contract.ts`: the `'muse'`
 * variant's "`slug: null` = none chosen, the column opens in the choose state"), so a house-agnostic
 * `Column`/`Sheet` can disarm the verb without knowing anything about muses. `{kind:'none'}`
 * (Toile), a resolved `'model'`, and a `'muse'` with a slug already chosen all arm as they always
 * have; only a museless `'muse'` identity disarms. `undefined`/`null` — a response shape from before
 * `identity` existed, or a house that hasn't wired one — reads as armable, the unchanged behaviour.
 */
export function identityArmable(identity?: Identity | null): boolean {
    if (!identity) return true
    return identity.kind !== 'muse' || identity.slug !== null
}

/**
 * The identity the wall acts under. A visitor at a house that supplies an intake has no model by
 * definition, so they read as a member whose model is missing: the verb says what it will do (create
 * the model) and opens the intake, where sign-in comes at Start. Anywhere else the response's own
 * identity stands, `{kind:'none'}` before `/mine` has answered.
 */
export function wallIdentity(fromMine: Identity | undefined, signedIn: boolean, hasIntake: boolean): Identity {
    if (!signedIn && hasIntake) {
        return {kind: 'model', state: 'missing', eta_ms: null, percent: null, thumb_key: null, name: null}
    }
    return fromMine ?? {kind: 'none'}
}

/**
 * The state a `402 allowance-exhausted` answer (`{error, limit, hasPlan, resetsAt}`) says the reader stands in: none
 * left. Null for any other answer. The wall folds it into `/mine`'s `allowance`, which the bar draws.
 */
export function allowanceRefusal(status: number, body: unknown): AllowanceState | null {
    if (status !== 402 || !body || typeof body !== 'object') return null
    const {error, limit, hasPlan, resetsAt} = body as {error?: unknown; limit?: unknown; hasPlan?: unknown; resetsAt?: unknown}
    if (error !== 'allowance-exhausted') return null
    return {
        remaining: 0,
        limit: typeof limit === 'number' ? limit : 0,
        hasPlan: hasPlan === true,
        resetsAt: typeof resetsAt === 'string' ? resetsAt : null,
    }
}

export function errorLineFor(status: number, code?: ErrorCode | null, field?: string | null): string {
    if (status === 0 || status >= 500) return 'failed'
    switch (code) {
        case 'rate_limited':
            return 'try again in a minute'
        case 'invalid_body':
            return field === 'direction' ? 'too long' : 'not accepted'
        case 'not_found':
            return 'gone'
        case 'identity_required': // Ovid `/continue`'s own new-chain museless refusal answers this
                                   // same code (contract.ts's own comment) — one concept, one line.
            return 'choose her first'
        case 'conflict':
            return 'already sent'
        case 'not_signed_in':
            return 'sign in first'
        // The wall's payment hook (`HouseAdapter.canWrite`, `house.ts`) — Narcis before its model is
        // paid; no other house defines it. A fixed line, house-agnostic like `identity_required`'s
        // 'choose her first' above (narcis wall plan re-review fix F3 — without this case the code
        // fell through to `default`'s 'failed', which reads as a render error rather than "payment
        // needed").
        case 'credits_required':
            return 'payment needed'
        // A spent picture allowance (Ovid): the pill's word; the column's richer line is `allowanceRefusal`'s.
        case 'allowance-exhausted':
            return 'daily limit reached'
        default:
            if (status === 429) return 'try again in a minute'
            if (status === 404) return 'gone'
            if (status === 400) return 'not accepted'
            if (status === 402) return 'payment needed'
            return 'failed'
    }
}

/**
 * The pill's line for a row the ARM failed (as opposed to `errorLineFor`'s `/continue` that never
 * landed). Same vocabulary as every other pill line — short, lowercase, no punctuation:
 * `fail_reason = 'refused'` gets the house's own line (`theme.verbs.refused` — "the direction
 * rewriter declined," never the model's own code); `'identity_unavailable'` gets the SAME line
 * `errorLineFor`'s `identity_required` uses, `'choose her first'` (review item G, adversarial pass
 * on wall verbs, 2026-09-17) — a row whose `museSlug` no longer resolves at claim time
 * (`ovidHouse`'s own `IdentityRefusedError`, `house.server.ts:51`) is the same "you have no muse
 * pinned" situation as a refused `/continue`, not a generic failure the reader can do nothing
 * about; every other reason keeps today's word, `failed` (`Wall.tsx`'s own fresh-failure notice,
 * kept in this file per plan § Files: "the pill vocabulary stays in one file").
 */
export function failedLineFor(take: Pick<Take, 'fail_reason'>, theme: HouseTheme): string {
    if (take.fail_reason === 'refused') return theme.verbs.refused
    if (take.fail_reason === 'identity_unavailable') return 'choose her first'
    return 'failed'
}

/**
 * The kind a `Notice` (`@core/ui/Notice`) draws a pill/column/sheet/page-verb line in — a pure
 * function of the line's own words, per the BRIEF's decided split (`thinking/design/toile_wall/
 * BRIEF.md` § Failure states, drawn 2026-09-01): kinds split by what the reader can do about it,
 * not by which function produced the string.
 *
 * `error` — the ask did not happen and pressing again changes nothing: `errorLineFor`'s `failed`/
 * `too long`/`not accepted`/`gone`, and every house's own refused line (`theme.verbs.refused`) —
 * Toile's `not something we draw`, Narcis's `not a portrait we make`, Ovid's `not in her
 * repertoire` (canon as of this session; the theme itself lands in plans f/g). `warning` — the
 * work waits on time, a sign-in or a choice: `try again in a minute`, `sign in first`, `choose her
 * first`, `already sent`, and a training/pause line (Narcis's future `training paused ·
 * retrying`). `success` — `N ready`, the wall's one piece of good news. Everything else (the
 * in-flight `generating…` lines) is `info` — a fact that nothing failed and nothing waits on.
 */
export function noticeKindFor(line: string): NoticeKind {
    if (NOTICE_ERROR_LINES.has(line) || REFUSED_LINES.has(line)) return 'error'
    if (NOTICE_WARNING_LINES.has(line) || /training|pause/i.test(line)) return 'warning'
    if (/ ready$/.test(line)) return 'success'
    return 'info'
}

const NOTICE_ERROR_LINES = new Set(['failed', 'too long', 'not accepted', 'gone'])
const NOTICE_WARNING_LINES = new Set(['try again in a minute', 'sign in first', 'choose her first', 'already sent', 'payment needed', 'daily limit reached'])
/** A fixed, closed vocabulary, not a pattern (BRIEF § "the three houses' own line"): `noticeKindFor`
 *  has no `HouseTheme` to read, so the words themselves are the only handle it has on which house
 *  said them. */
const REFUSED_LINES = new Set(['not something we draw', 'not a portrait we make', 'not in her repertoire'])

/**
 * What a muse of her own, just queued, arms when the intake closes. The muse being made is shown in the bar's strip,
 * and the bar exists only while something is armed: a return from the payment page reopens the intake with nothing
 * armed unless the buyer stood on a tile, so the ghost is armed — she sees "being made" instead of the bare wall.
 * Null where the identity is not a muse (Narcis's training shows on the identity itself) or something is armed.
 */
export function armAfterQueuedMuse(identityKind: Identity['kind'], armed: Armed | null): Armed | null {
    return identityKind === 'muse' && armed === null ? {kind: 'ghost'} : null
}

/**
 * B1 (failure-logging review, amended): the pill sits at a LOWER stacking layer than the
 * column/sheet scrim (`--tl-z-pill` below `--tl-z-arena`, `products/toile/styles/index.scss` — the
 * wall's stacking order, one source) and the scrim takes pointer events — showing the pill while an
 * overlay is open does not surface a notice, it renders it invisibly and unreachably behind the
 * scrim. The honest fix is not to raise the pill above the scrim; it is to route the notice into the
 * OPEN OVERLAY's own note slot (Column's `note` prop / Sheet's own), the one surface actually on
 * screen, whether the notice belongs to the armed chain or to a different one — "a failure on
 * another chain is visible" (the review's own phrase) has nowhere else to land while an overlay is
 * up.
 */
export function noteForOverlay(overlayOpen: boolean, notice: {key: ChainKey; line: string} | null): string | null {
    return overlayOpen && notice ? notice.line : null
}

/**
 * C3 (BRIEF § Docked: "Tap or Continue ↑ unfolds the column on the newest finished take — during a
 * batch, before any cell has finished, it scrolls the wall back to the top where the nine are
 * rendering"). Continue ↑ opens the column only once there is a finished take of the batch to show
 * it on; while every one of the batch's nine cells is still queued or rendering, there is nothing
 * for the column to open on, and the pending rows are already lifted to the top of the wall (the
 * arrival ordering, `unseenDone`/the wall's own lift) — that is where "the nine are rendering."
 * `batchId` is `null` for a single (never a batch), which always opens the column.
 */
export function pillContinueAction(mine: MineResponse | null, batchId: string | null): 'scroll_to_top' | 'open_column' {
    if (!batchId) return 'open_column'
    const anyDone = batchTakes(mine, batchId).some((row) => row.status === 'done')
    return anyDone ? 'open_column' : 'scroll_to_top'
}

/**
 * C2: the field's placeholder names what typing into it DOES, which differs by whether there is
 * already a picture to edit. `step` is the step on screen (`displayedStep`) — non-null whenever a
 * basis tile or a take is there (a tile chain always has its basis as step 1, so this is non-null
 * for every tile chain regardless of whether it has been twisted yet); `null` only for a
 * from-scratch chain with nothing rendered — the ghost's own armed state, where there is nothing
 * yet to change and the field is instead proposing what to make.
 */
export function fieldPlaceholder(step: ChainStep | null, placeholder: {onPicture: string; onBlank: string}): string {
    return step ? placeholder.onPicture : placeholder.onBlank
}

/**
 * The first `maxWords` words of free text, trimmed and single-spaced — both what a direction chip
 * shows and, since a chip's tap hands its own label straight to `setQ` (board `WordsAsDoors` row 2
 * frame 4), what ends up searched. A long direction is cut once, here, rather than at the display
 * site and the search site separately.
 */
export function cutWords(text: string, maxWords = 4): string {
    return text.trim().split(/\s+/).filter(Boolean).slice(0, maxWords).join(' ')
}

/**
 * The doors a caption or a hover label renders (board `WordsAsDoors`, "How a word says it is a
 * door" / "A take — its basis's words, then yours"): a basis tile's own chips, then the take on
 * screen's typed direction as one more door, cut to its first four words. `[]` when the tile has no
 * chips of its own (every Ovid tile today ships `chips = []`) and the take carries no direction
 * either — the caller falls back to plain text in that case, per the same board: "a tile with no
 * chips … not a door, not a link." One definition for every caller: the armed column/sheet caption,
 * a from-scratch chain's own cell, and a pinned cell's own view of one step.
 */
export function captionDoors(tile: TileSummary | null, direction: string | null | undefined): string[] {
    const words = tile ? [...tile.chips] : []
    const cut = direction ? cutWords(direction) : ''
    if (cut) words.push(cut)
    return words
}

/**
 * Whether a door tap folds the open column/sheet (board `WordsAsDoors`, "the column/sheet folds
 * exactly as every other return does"): the armed caption's own tap returns, like every other
 * gesture that closes the layer; an unarmed tile's hover chip has nothing open to fold — "sets the
 * query without arming."
 */
export function wordTapFolds(from: WallWordTappedFrom): boolean {
    return from === 'caption'
}
